import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import listReportsApi from '@/apis/list-reports.api';
import PATH from '@/constants/path';
import QUERY_KEYS from '@/constants/query-keys';
import useDestination from '@/hooks/use-destination.hook';
import useUser from '@/hooks/use-user.hook';
import { Report, ReportStatus } from '@/types/report';

export default function useGetReport() {
  const { getToken } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLocateName } = useDestination();
  const previousReports = useRef<Report[]>([]);

  const queryFunction = useCallback(async () => {
    const token = await getToken();
    const resp = await listReportsApi(token);
    const results = resp.data
      .map<Report>(i => ({
        ...i,
        familyMembers: i.familyMembers?.split(','),
        pet: i.pet?.split(','),
        createdAt: new Date(i.createdAt),
        updatedAt: new Date(i.updatedAt),
      }))
      .sort(
        (a: Report, b: Report) => b.createdAt.getTime() - a.createdAt.getTime()
      );
    if (previousReports.current.length > 0) {
      results?.forEach((report: Report) => {
        const previousReport = previousReports.current.find(
          prev => prev.id === report.id
        );
        if (
          previousReport?.status !== ReportStatus.FINISHED &&
          report.status === ReportStatus.FINISHED
        ) {
          toast.success(
            t('use_report.success.finished', {
              destination: getLocateName(report.targetDestination),
            }),
            {
              duration: 8_000,
              action: {
                label: t('use_report.actions.view_report'),
                onClick: () => navigate(PATH.DASHBOARD.REPORT(report.id)),
              },
            }
          );
        }
      });
    }
    return results;
  }, [getLocateName, getToken, navigate, t]);

  const {
    data: reports,
    isPending: isPendingReports,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.REPORTS.LIST,
    queryFn: queryFunction,
    // every 5 seconds if there are pending reports, otherwise do not refetch
    refetchInterval: previousReports.current.some(
      report =>
        report.status === ReportStatus.PENDING ||
        report.status === ReportStatus.IN_PROGRESS
    )
      ? 1000 * 5
      : false,
    // 5 minutes as the staleTime if there are no pending reports
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => {
    previousReports.current = reports ?? [];
  }, [reports]);

  useEffect(() => {
    if (error) {
      toast.error(t('use_report.error.fetch'));
    }
  }, [error, t]);

  return {
    isPendingReports,
    reports: reports ?? [],
  };
}
